import { parseFlagValues, type FlagValues } from '@core/access/flags';

import { GLOBAL_SCOPE, type ScopeNode } from './scopes';

/**
 * The organisation's settings, as stored.
 *
 * One document: per-scope overrides for feature flags, formats and rules, the
 * countries added beyond the built-in tree, each role's defaults, and the
 * history of saved changes. Every value in a scope is an override - a scope
 * that says nothing about a setting inherits it - so an empty document is a
 * valid one and means "everything as shipped".
 *
 * Formats and rules are stored as plain keyed values here. What the keys mean,
 * and what they change, is defined where they take effect: the valuation
 * profiles (`@features/valuations/profiles/scoped-settings`).
 */

export type RuleParamValue = number | string;

export interface RuleOverride {
  enabled?: boolean;
  params?: Record<string, RuleParamValue>;
}

export interface ScopeSettings {
  flags?: FlagValues;
  formats?: Record<string, RuleParamValue>;
  rules?: Record<string, RuleOverride>;
}

export interface RoleDefault {
  /** Route the role lands on when it opens the portal. */
  landing?: string;
  /** The Assignments layout it starts in. */
  assignmentsView?: string;
}

export interface SettingsChange {
  /** ISO timestamp. */
  at: string;
  by: string;
  /** One line per value that changed, e.g. "Report Studio · Singapore · on". */
  changes: string[];
}

export interface SettingsDocument {
  version: 1;
  scopes: Record<string, ScopeSettings>;
  /** Countries added on the Settings page, beyond the built-in tree. */
  countries: ScopeNode[];
  /** Keyed by portal role. */
  roleDefaults: Record<string, RoleDefault>;
  /** Newest first. */
  history: SettingsChange[];
}

export const EMPTY_SETTINGS: SettingsDocument = {
  version: 1,
  scopes: {},
  countries: [],
  roleDefaults: {},
  history: []
};

/** How many saved changes the history keeps. */
export const HISTORY_LIMIT = 100;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function scalarMap(input: unknown): Record<string, RuleParamValue> | undefined {
  if (!isRecord(input)) {
    return undefined;
  }

  const out: Record<string, RuleParamValue> = {};

  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'number' || typeof value === 'string') {
      out[key] = value;
    }
  }

  return out;
}

/**
 * Untrusted input - storage, an API response - to a document. Anything
 * malformed is dropped rather than thrown on: a bad entry in one scope must
 * not cost every user their portal.
 */
export function parseSettingsDocument(input: unknown): SettingsDocument {
  if (!isRecord(input)) {
    return EMPTY_SETTINGS;
  }

  const scopes: Record<string, ScopeSettings> = {};

  if (isRecord(input.scopes)) {
    for (const [id, raw] of Object.entries(input.scopes)) {
      if (!isRecord(raw)) {
        continue;
      }

      const rules: Record<string, RuleOverride> = {};

      if (isRecord(raw.rules)) {
        for (const [ruleId, rule] of Object.entries(raw.rules)) {
          if (isRecord(rule)) {
            rules[ruleId] = {
              ...(typeof rule.enabled === 'boolean' ? { enabled: rule.enabled } : {}),
              ...(scalarMap(rule.params) ? { params: scalarMap(rule.params) } : {})
            };
          }
        }
      }

      scopes[id] = {
        flags: parseFlagValues(raw.flags, `settings scope ${id}`),
        formats: scalarMap(raw.formats) ?? {},
        rules
      };
    }
  }

  const countries = Array.isArray(input.countries)
    ? input.countries.filter(
        (node): node is ScopeNode =>
          isRecord(node) &&
          typeof node.id === 'string' &&
          typeof node.label === 'string' &&
          typeof node.parent === 'string' &&
          node.level === 'country' &&
          node.id !== GLOBAL_SCOPE
      )
    : [];

  const roleDefaults: Record<string, RoleDefault> = {};

  if (isRecord(input.roleDefaults)) {
    for (const [role, raw] of Object.entries(input.roleDefaults)) {
      if (isRecord(raw)) {
        roleDefaults[role] = {
          ...(typeof raw.landing === 'string' ? { landing: raw.landing } : {}),
          ...(typeof raw.assignmentsView === 'string' ? { assignmentsView: raw.assignmentsView } : {})
        };
      }
    }
  }

  const history = Array.isArray(input.history)
    ? input.history
        .filter(
          (entry): entry is SettingsChange =>
            isRecord(entry) && typeof entry.at === 'string' && typeof entry.by === 'string' && Array.isArray(entry.changes)
        )
        .slice(0, HISTORY_LIMIT)
    : [];

  return { version: 1, scopes, countries, roleDefaults, history };
}

/** One scope's overrides, never undefined. */
export function scopeSettings(document: SettingsDocument, scopeId: string): ScopeSettings {
  return document.scopes[scopeId] ?? {};
}
